import React from 'react';

const SmartContextCards = React.memo(({ context, vertical, coachingType, onCardClick, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="ov-smart-context-cards">
        {[1, 2, 3].map(i => (
          <div key={i} className="ov-context-card-skeleton">
            <div className="ov-skeleton-context-header">
              <div className="ov-loading-skeleton ov-skeleton-context-icon"></div>
              <div className="ov-loading-skeleton ov-skeleton-context-label"></div>
            </div>
            <div className="ov-loading-skeleton ov-skeleton-context-value"></div>
            <div className="ov-loading-skeleton ov-skeleton-context-hint"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="ov-error-state">
        <div className="ov-error-icon">⚠️</div>
        <div className="ov-error-title">Error loading context</div>
        <div className="ov-error-message">{error}</div>
        <button 
          className="ov-error-retry"
          onClick={() => window.location.reload()}
          aria-label="Retry loading context data"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!context || Object.keys(context).length === 0) {
    return (
      <div className="ov-empty-state">
        <div className="ov-empty-icon">💬</div>
        <div className="ov-empty-title">No data yet</div>
        <div className="ov-empty-description">Start a conversation to see contextual insights appear here</div>
      </div>
    );
  }
  
  const getFieldLabel = (key) => {
    const labels = {
      revenue: '💰 Annual Revenue',
      arr: '💰 ARR',
      time_spent: '⏱️ Hours/Week', 
      hoursPerWeek: '⏱️ Hours/Week',
      team_size: '👥 Team Size',
      teamSize: '👥 Team Size',
      goals: '🎯 Goals',
      challenges: '🚨 Challenges',
      challenge: '🚨 Challenge',
      painPoints: '💡 Pain Points',
      process: '⚙️ Process',
      current_weight: '⚖️ Current Weight',
      goal_weight: '🎯 Goal Weight',
      equipment_type: '🔧 Equipment',
      safety_concerns: '⚠️ Safety Issues',
      marketing: '📈 Marketing',
      retention: '🔄 Retention',
      acquisition: '🎯 Acquisition',
      ltv: '💎 LTV',
      cac: '💰 CAC'
    };
    return labels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const getCardColor = (key) => {
    const colors = {
      revenue: 'ov-context-card-revenue',
      arr: 'ov-context-card-revenue',
      time_spent: 'ov-context-card-time',
      hoursPerWeek: 'ov-context-card-time',
      team_size: 'ov-context-card-team',
      teamSize: 'ov-context-card-team',
      goals: 'ov-context-card-goals',
      challenges: 'ov-context-card-challenges',
      challenge: 'ov-context-card-challenges',
      painPoints: 'ov-context-card-challenges',
      process: 'ov-context-card-process',
      current_weight: 'ov-context-card-health',
      goal_weight: 'ov-context-card-health',
      equipment_type: 'ov-context-card-equipment',
      safety_concerns: 'ov-context-card-safety',
      marketing: 'ov-context-card-marketing',
      retention: 'ov-context-card-retention',
      acquisition: 'ov-context-card-acquisition',
      ltv: 'ov-context-card-ltv',
      cac: 'ov-context-card-cac'
    };
    return colors[key] || 'ov-context-card-default';
  };
  
  return (
    <div className="ov-smart-context-cards" role="region" aria-label="Conversation insights">
      {Object.entries(context).slice(0, 4).map(([key, value]) => (
        <div 
          key={key} 
          className={`ov-context-card ${getCardColor(key)}`}
          onClick={() => onCardClick && onCardClick(key, value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onCardClick && onCardClick(key, value);
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={`${getFieldLabel(key)}: ${Array.isArray(value) ? value.join(', ') : String(value)}`}
        >
          <div className="ov-context-card-header">
            <span className="ov-context-card-icon" aria-hidden="true">
              {getFieldLabel(key).split(' ')[0]}
            </span>
            <span className="ov-context-card-label">
              {getFieldLabel(key).split(' ').slice(1).join(' ')}
            </span>
          </div>
          <div className="ov-context-card-value">
            {Array.isArray(value) ? value.join(', ') : String(value)}
          </div>
          <div className="ov-context-card-hint">Click for details</div>
        </div>
      ))}
    </div>
  );
});

SmartContextCards.displayName = 'SmartContextCards';

export default SmartContextCards;
